/**
 * Preferenza client-side per la scelta fra immagine "reale" e screenshot
 * automatico sull'hero di un approfondimento.
 *
 * Contesto: il backend, allo stato attuale, espone un solo campo `immagine`
 * su `ApprofondimentoDto` e il suo `CreateApprofondimentoRequest` non accetta
 * un `immagineId`; di conseguenza non c'è modo di:
 *   - caricare un'immagine manuale distinta dallo screenshot dall'admin;
 *   - persistere lato server un flag del tipo `useScreenshotAsHero`.
 *
 * Finché il backend non viene esteso, la scelta viene memorizzata in
 * `localStorage` (quindi vale solo sul browser dell'admin) e un
 * `storage`/`CustomEvent` tiene sincronizzati i componenti aperti.
 *
 * TODO backend: aggiungere un campo `heroImagePreference` (AUTO | HIDE | USE)
 *               su `ApprofondimentoDto` + corrispondente su
 *               `CreateApprofondimentoRequest`, e un endpoint per caricare
 *               un'immagine "manuale" distinta dallo screenshot.
 */

import type { MediaDto } from "@/lib/api";

const STORAGE_PREFIX = "hero-approf:";
const EVENT_NAME = "hero-approf-pref-changed";

export type HeroImagePreference = "auto" | "show" | "hide";

function storageKey(id: number): string {
  return `${STORAGE_PREFIX}${id}`;
}

/** Ritorna true se il media è (euristicamente) lo screenshot generato dal backend. */
export function isScreenshotMedia(media: MediaDto | null | undefined): boolean {
  if (!media) return false;
  const fn = (media.filename || "").toLowerCase();
  const url = (media.url || "").toLowerCase();
  // Il backend salva con pattern uuid-screenshot…png
  return fn.includes("screenshot") || url.includes("screenshot");
}

/** Legge la preferenza salvata. In SSR restituisce sempre "auto". */
export function getHeroImagePreference(approfondimentoId: number): HeroImagePreference {
  if (typeof window === "undefined") return "auto";
  try {
    const v = window.localStorage.getItem(storageKey(approfondimentoId));
    if (v === "show" || v === "hide") return v;
  } catch { /* ignore */ }
  return "auto";
}

/** Scrive la preferenza (o la rimuove se "auto") e notifica i listener. */
export function setHeroImagePreference(approfondimentoId: number, pref: HeroImagePreference) {
  if (typeof window === "undefined") return;
  try {
    if (pref === "auto") {
      window.localStorage.removeItem(storageKey(approfondimentoId));
    } else {
      window.localStorage.setItem(storageKey(approfondimentoId), pref);
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { id: approfondimentoId, pref } }));
  } catch { /* ignore */ }
}

/**
 * Decide se l'immagine va usata nell'hero in base a preferenza + euristica
 * screenshot. Default: se non esiste preferenza esplicita, lo screenshot
 * viene mostrato (è meglio di niente, coerente col comportamento attuale).
 */
export function shouldUseHeroImage(
  approfondimentoId: number | undefined,
  media: MediaDto | null | undefined,
): boolean {
  if (!media) return false;
  if (!approfondimentoId) return true;
  const pref = getHeroImagePreference(approfondimentoId);
  if (pref === "show") return true;
  if (pref === "hide") return false;
  return true; // auto → mostra
}

/** Sottoscrive i cambi di preferenza (solo client). Ritorna funzione di cleanup. */
export function subscribeHeroImagePreference(
  cb: (id: number, pref: HeroImagePreference) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => {
    const ce = e as CustomEvent<{ id: number; pref: HeroImagePreference }>;
    if (ce.detail) cb(ce.detail.id, ce.detail.pref);
  };
  const storageHandler = (e: StorageEvent) => {
    if (!e.key || !e.key.startsWith(STORAGE_PREFIX)) return;
    const id = Number(e.key.slice(STORAGE_PREFIX.length));
    if (!Number.isFinite(id)) return;
    const pref: HeroImagePreference =
      e.newValue === "show" || e.newValue === "hide" ? e.newValue : "auto";
    cb(id, pref);
  };
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener("storage", storageHandler);
  };
}

